import '../../syncfusionStyles';

import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useLazyQuery, gql } from '@apollo/client';
import { format, parseISO } from 'date-fns';
import {
  SAVE_ANNOTATION,
  GET_ANNOTATIONS,
  GET_PDF_CATEGORIES,
  DELETE_ANNOTATION,
} from '../../graphql/types/annotation';

import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { DetailsView, FileSelectEventArgs, NavigationPane } from '@syncfusion/ej2-react-filemanager';
import {
  Annotation,
  BookmarkView,
  FormDesigner,
  FormFields,
  Inject,
  LinkAnnotation,
  Magnification,
  Navigation,
  PdfViewerComponent,
  Print,
  // TextSearch,  // Temporarily disabled to prevent initialization errors
  TextSelection,
  ThumbnailView,
  Toolbar,
  AnnotationSettingsModel,
} from '@syncfusion/ej2-react-pdfviewer';

import i18n from '../../locales/i18n';
import { useSnackbar } from '../../providers/SnackbarProvider';

import StyledFileManager from './components/StyledFileManager';
import { ContextMenuItem, FileType } from './types';
import { GDriveConfigDialog, GDriveConfigEstimateType } from '../../components/GDriveConfigDialog';
import InfoIcon from '@mui/icons-material/Info';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import TextField from '@mui/material/TextField';

const pdfContextMenuItems = [
  { id: ContextMenuItem.MARK_AS_IM, text: 'Mark as IM' },
  { id: ContextMenuItem.MARK_AS_SHAREHOLDER, text: 'Mark as Shareholder List' },
  { id: ContextMenuItem.UNMARK_AS_SHAREHOLDER, text: 'Unmark as Shareholder List' },
];

const excelContextMenuItems = [
  { id: ContextMenuItem.MARK_AS_XLS_CUSTOMER, text: 'Mark as Customer List' },
  { id: ContextMenuItem.UNMARK_AS_XLS_CUSTOMER, text: 'Unmark as Customer List' },
  { id: ContextMenuItem.MARK_AS_DA_UPLOAD, text: 'Mark as DA Upload' },
  { id: ContextMenuItem.UNMARK_AS_DA_UPLOAD, text: 'Unmark as DA Upload' },
];

// Helper function to format file sizes
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Helper function to format dates
const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  try {
    const date = parseISO(dateString);
    return format(date, 'dd MMM yyyy HH:mm');
  } catch {
    return '';
  }
};

const GET_COMPANY_GDRIVE_INFO = gql`
  query GetCompanyGDriveInfo($companyId: Int!) {
    getCompany(companyId: $companyId, first: 1) {
      edges {
        node {
          id
          shortName
          legalName
          gdriveSharedDriveName
          gdriveDirectoryName
          gdriveSyncStatus {
            lastSyncRequested
            lastSyncCompleted
            totalFilesSynced
            lastFileModified
            isSyncing
            cooldownEndsAt
          }
        }
      }
    }
  }
`;

const GET_AVAILABLE_DRIVES = gql`
  query GetAvailableGoogleDrives {
    availableGoogleDrives {
      name
    }
  }
`;

const UPDATE_GDRIVE_CONFIG = gql`
  mutation UpdateCompanyGoogleDriveConfig(
    $companyId: Int!
    $gdriveSharedDriveName: String
    $gdriveDirectoryName: String
  ) {
    updateCompanyGoogleDriveConfig(
      companyId: $companyId
      gdriveSharedDriveName: $gdriveSharedDriveName
      gdriveDirectoryName: $gdriveDirectoryName
    ) {
      success
      message
    }
  }
`;

const REFRESH_GDRIVE_FILES = gql`
  mutation RefreshCompanyGoogleDriveFiles($companyId: Int!) {
    refreshCompanyGoogleDriveFiles(companyId: $companyId) {
      success
      message
      autoConfigured
      requiresConfiguration
      configEstimate {
        sharedDriveName
        directoryName
        directoryId
        confidence
        alternatives
        requiresUserConfirmation
      }
    }
  }
`;

const CompanyFilesPage = () => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const companyId = queryParams.get('companyId');

  const [selectedFileDetails, setSelectedFileDetails] = useState<FileType | null>(null);
  const [pdfFileUrl, setPdfFileUrl] = useState<string | null>(null);
  const [currentPdfId, setCurrentPdfId] = useState<number | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const annotationsLoadedRef = useRef<boolean>(false);
  const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);
  const [fileIndicatorTrigger, setFileIndicatorTrigger] = useState<number>(0);

  const [selectedArea, setSelectedArea] = useState('');
  const [selectedCategory1, setSelectedCategory1] = useState('');
  const [selectedCategory2, setSelectedCategory2] = useState('');
  const [selectedCategory3, setSelectedCategory3] = useState('');

  // GDrive configuration state
  const [isEditingDirectory, setIsEditingDirectory] = useState(false);
  const [directoryInput, setDirectoryInput] = useState('');
  const [lastSyncTriggered, setLastSyncTriggered] = useState<Date | null>(null);
  const fileManagerRef = useRef<any>(null);
  const prevSyncCompletedRef = useRef<string | null>(null);

  // GDrive auto-config dialog state
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [configEstimate, setConfigEstimate] = useState<GDriveConfigEstimateType | null>(null);

  const { data: categoriesData } = useQuery(GET_PDF_CATEGORIES, {
    onError: error => {
      console.error('Error loading categories:', error);
      showSnackbar(t('companyFilesPage.categoriesLoadError'), 'error', false);
    },
  });

  const rawCategories = categoriesData?.pdfDocumentAnnotationsMenuCategories || [];

  const allAreas = rawCategories.map(item => item.area);
  const areas = allAreas.filter((area, index) => allAreas.indexOf(area) === index);

  const categories1 = selectedArea
    ? rawCategories
        .filter(cat => cat.area === selectedArea)
        .map(cat => cat.category1)
        .filter((cat, index, arr) => arr.indexOf(cat) === index)
    : [];

  const categories2 = selectedCategory1
    ? rawCategories
        .filter(cat => cat.area === selectedArea && cat.category1 === selectedCategory1)
        .map(cat => cat.category2)
        .filter(Boolean)
        .filter((cat, index, arr) => arr.indexOf(cat) === index)
    : [];

  const categories3 = selectedCategory2
    ? rawCategories
        .filter(
          cat => cat.area === selectedArea && cat.category1 === selectedCategory1 && cat.category2 === selectedCategory2
        )
        .map(cat => cat.category3)
        .filter(Boolean)
        .filter((cat, index, arr) => arr.indexOf(cat) === index)
    : [];

  const currentQuestion =
    selectedArea && selectedCategory1
      ? rawCategories.find(
          item =>
            item.area === selectedArea &&
            item.category1 === selectedCategory1 &&
            (item.category2 === selectedCategory2 || (!item.category2 && !selectedCategory2)) &&
            (item.category3 === selectedCategory3 || (!item.category3 && !selectedCategory3))
        )?.question
      : '';

  const hostUrl = process.env.REACT_APP_FILESERVER_BACKEND;

  // Fetch company GDrive info
  const { data: companyData, startPolling } = useQuery(GET_COMPANY_GDRIVE_INFO, {
    variables: { companyId: companyId ? parseInt(companyId) : 0 },
    skip: !companyId,
    pollInterval: 60000, // Default: slow poll when idle
    onError: error => {
      console.error('Error fetching company GDrive info:', error);
    },
  });

  const companyNode = companyData?.getCompany?.edges?.[0]?.node;
  const gdriveSyncStatus = companyNode?.gdriveSyncStatus;

  // Available drives query
  const { data: drivesData } = useQuery(GET_AVAILABLE_DRIVES);
  const availableDrives = drivesData?.availableGoogleDrives || [];

  // Mutations
  const [updateGDriveConfig] = useMutation(UPDATE_GDRIVE_CONFIG, {
    refetchQueries: [{ query: GET_COMPANY_GDRIVE_INFO, variables: { companyId: companyId ? parseInt(companyId) : 0 } }],
    onCompleted: data => {
      if (data.updateCompanyGoogleDriveConfig.success) {
        showSnackbar('Google Drive configuration updated', 'success', false);
      } else {
        showSnackbar(data.updateCompanyGoogleDriveConfig.message || 'Failed to update configuration', 'error', false);
      }
    },
    onError: error => {
      console.error('Error updating GDrive config:', error);
      showSnackbar('Failed to update Google Drive configuration', 'error', false);
    },
  });

  const [refreshGDriveFiles] = useMutation(REFRESH_GDRIVE_FILES, {
    refetchQueries: [{ query: GET_COMPANY_GDRIVE_INFO, variables: { companyId: companyId ? parseInt(companyId) : 0 } }],
    onCompleted: data => {
      const result = data.refreshCompanyGoogleDriveFiles;

      if (result.requiresConfiguration && result.configEstimate) {
        // Company needs GDrive configuration - show dialog
        setConfigEstimate(result.configEstimate);
        setShowConfigDialog(true);
      } else if (result.autoConfigured) {
        // Auto-configured successfully
        showSnackbar(
          `Google Drive auto-configured: ${result.configEstimate?.sharedDriveName}/${result.configEstimate?.directoryName}`,
          'success',
          false
        );
      } else if (result.success) {
        showSnackbar('Google Drive sync started', 'success', false);
      } else {
        showSnackbar(result.message || 'Failed to start sync', 'warning', false);
      }
    },
    onError: error => {
      console.error('Error refreshing GDrive files:', error);
      showSnackbar('Failed to start Google Drive sync', 'error', false);
    },
  });

  const [saveAnnotation, { loading: isSaving }] = useMutation(SAVE_ANNOTATION, {
    onCompleted: () => {
      showSnackbar(t('companyFilesPage.annotationsSaved'), 'success', false);
    },
    onError: error => {
      console.error('Error saving annotations:', error);
      showSnackbar(t('companyFilesPage.annotationsSaveError'), 'error', false);
    },
  });

  const [deleteAnnotation] = useMutation(DELETE_ANNOTATION, {
    onCompleted: () => {
      showSnackbar('Annotation deleted successfully', 'success', false);
    },
    onError: error => {
      console.error('Error deleting annotation:', error);
      showSnackbar('Error deleting annotation', 'error', false);
    },
  });

  const [getAnnotations, { loading: isLoadingAnnotations }] = useLazyQuery(GET_ANNOTATIONS, {
    onError: error => {
      console.error('Error loading annotations:', error);
      showSnackbar(t('companyFilesPage.annotationsLoadError'), 'error', false);
    },
  });

  // GDrive handlers - non-blocking (fire-and-forget)
  const handleDriveSelect = (driveName: string) => {
    if (!companyId) return;
    // Don't await - let mutation run in background
    updateGDriveConfig({
      variables: {
        companyId: parseInt(companyId),
        gdriveSharedDriveName: driveName,
      },
    });
  };

  const handleDirectorySave = () => {
    if (!companyId || !directoryInput.trim()) return;
    // Don't await - let mutation run in background
    updateGDriveConfig({
      variables: {
        companyId: parseInt(companyId),
        gdriveDirectoryName: directoryInput.trim(),
      },
    });
    setIsEditingDirectory(false);
    setDirectoryInput('');
  };

  const handleRefreshSync = () => {
    if (!companyId) return;
    setLastSyncTriggered(new Date());
    // Don't await - sync runs asynchronously in Celery
    refreshGDriveFiles({
      variables: {
        companyId: parseInt(companyId),
      },
    });
  };

  const handleConfigDialogClose = () => {
    setShowConfigDialog(false);
    setConfigEstimate(null);
  };

  const handleConfigConfirm = () => {
    // After config is confirmed, trigger a sync to actually load the files
    if (companyId) {
      setLastSyncTriggered(new Date());
      refreshGDriveFiles({
        variables: {
          companyId: parseInt(companyId),
        },
      });
    }
  };

  // Check if cooldown is active
  const isCooldownActive = gdriveSyncStatus?.cooldownEndsAt
    ? new Date(gdriveSyncStatus.cooldownEndsAt) > new Date()
    : false;

  // Derived sync states used to drive the file manager visibility
  const hasNeverSynced = !gdriveSyncStatus?.lastSyncCompleted;
  const isSyncingActive = gdriveSyncStatus?.isSyncing === true || lastSyncTriggered !== null;
  // Replace file manager with a full overlay when this is the very first sync
  const showFirstSyncOverlay = hasNeverSynced && isSyncingActive;
  // Banner + linear progress when re-syncing files that were already synced before
  const showResyncBanner = !hasNeverSynced && isSyncingActive;
  // Gentle nudge when drive is connected but files have never been synced
  const showNeverSyncedBanner = hasNeverSynced && !isSyncingActive && !!companyNode?.gdriveSharedDriveName;

  // Reload file manager after sync completes (with grace period)
  useEffect(() => {
    if (!lastSyncTriggered || !gdriveSyncStatus) return;

    // Check if sync completed after we triggered it
    const syncCompleted = gdriveSyncStatus.lastSyncCompleted ? new Date(gdriveSyncStatus.lastSyncCompleted) : null;

    const wasSyncing = gdriveSyncStatus.isSyncing;

    if (syncCompleted && syncCompleted > lastSyncTriggered && !wasSyncing) {
      // Sync completed! Wait a grace period then refresh file manager
      const refreshTimer = setTimeout(() => {
        if (fileManagerRef.current && fileManagerRef.current.ej2_instances && fileManagerRef.current.ej2_instances[0]) {
          console.log('Refreshing file manager after sync completion');
          fileManagerRef.current.ej2_instances[0].refreshFiles();
          setLastSyncTriggered(null); // Reset so we don't keep refreshing
        }
      }, 3000); // 3 second grace period

      return () => clearTimeout(refreshTimer);
    }
  }, [gdriveSyncStatus, lastSyncTriggered]);

  // Refresh file manager when sync completes from an external trigger (e.g. another tab, scheduled job)
  useEffect(() => {
    if (!gdriveSyncStatus) return;

    const currentSyncCompleted = gdriveSyncStatus.lastSyncCompleted ?? null;

    if (
      prevSyncCompletedRef.current !== null &&
      currentSyncCompleted !== null &&
      currentSyncCompleted !== prevSyncCompletedRef.current &&
      !gdriveSyncStatus.isSyncing &&
      !lastSyncTriggered // only handle externally-triggered syncs here
    ) {
      const refreshTimer = setTimeout(() => {
        if (fileManagerRef.current?.ej2_instances?.[0]) {
          console.log('Refreshing file manager after external sync completion');
          fileManagerRef.current.ej2_instances[0].refreshFiles();
        }
      }, 3000);

      prevSyncCompletedRef.current = currentSyncCompleted;
      return () => clearTimeout(refreshTimer);
    }

    prevSyncCompletedRef.current = currentSyncCompleted;
  }, [gdriveSyncStatus, lastSyncTriggered]);

  // Adaptive polling: fast while syncing, slow when idle
  useEffect(() => {
    startPolling(isSyncingActive ? 5000 : 60000);
  }, [isSyncingActive, startPolling]);

  const createDOMOverlay = (
    subjectText,
    x = '50%',
    y = '50%',
    dbId = null,
    saveAnnotationFn = null,
    uploadFileId = null
  ) => {
    const overlay = document.createElement('div');
    overlay.style.position = 'absolute';
    overlay.style.backgroundColor = '#FFFFCC';
    overlay.style.border = '1px solid #000';
    overlay.style.padding = '8px';
    overlay.style.fontSize = '12px';
    overlay.style.zIndex = '1000';
    overlay.style.left = x;
    overlay.style.top = y;
    overlay.style.transform = 'translate(-50%, -50%)';
    overlay.style.pointerEvents = 'auto';
    overlay.style.borderRadius = '4px';
    overlay.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
    overlay.style.fontFamily = 'Arial, sans-serif';
    overlay.style.cursor = 'move';
    overlay.style.userSelect = 'none';

    if (dbId) {
      overlay.setAttribute('data-db-id', dbId.toString());
    }

    overlay.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center;">
        <span>${subjectText}</span>
        <button style="
          background: #ff4444; 
          color: white; 
          border: none; 
          border-radius: 3px; 
          width: 20px; 
          height: 20px; 
          cursor: pointer; 
          font-size: 12px;
          margin-left: 10px;
        ">×</button>
      </div>
    `;

    const closeButton = overlay.querySelector('button');
    if (closeButton) {
      closeButton.addEventListener('click', async () => {
        if (dbId) {
          try {
            await deleteAnnotation({
              variables: { annotationId: parseInt(dbId.toString()) },
            });
            console.log('Annotation deleted from backend:', dbId);
          } catch (error) {
            console.error('Failed to delete annotation from backend:', error);
          }
        }

        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
      });
    }

    let isDragging = false;
    let startX, startY, offsetX, offsetY;

    overlay.addEventListener('mousedown', e => {
      if (e.target !== closeButton) {
        isDragging = true;

        const rect = overlay.getBoundingClientRect();

        offsetX = e.clientX - (rect.left + rect.width / 2);
        offsetY = e.clientY - (rect.top + rect.height / 2);

        startX = e.clientX;
        startY = e.clientY;

        const container = document.querySelector('#pdf-viewer .e-pv-viewer-container');
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2 - containerRect.left;
          const centerY = rect.top + rect.height / 2 - containerRect.top;

          overlay.style.left = centerX + 'px';
          overlay.style.top = centerY + 'px';
          overlay.style.transform = 'translate(-50%, -50%)';
        }

        e.preventDefault();
      }
    });

    document.addEventListener('mousemove', e => {
      if (isDragging) {
        const container = document.querySelector('#pdf-viewer .e-pv-viewer-container');
        if (container) {
          const containerRect = container.getBoundingClientRect();

          const newCenterX = e.clientX - offsetX - containerRect.left;
          const newCenterY = e.clientY - offsetY - containerRect.top;

          overlay.style.left = newCenterX + 'px';
          overlay.style.top = newCenterY + 'px';
        }
      }
    });

    document.addEventListener('mouseup', async () => {
      if (isDragging && dbId) {
        const rect = overlay.getBoundingClientRect();
        const container = document.querySelector('#pdf-viewer .e-pv-viewer-container');
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const relativeX = Math.round(rect.left + rect.width / 2 - containerRect.left);
          const relativeY = Math.round(rect.top + rect.height / 2 - containerRect.top);

          console.log('Saving new position for annotation', dbId, ':', {
            x: relativeX,
            y: relativeY,
            uploadFileId: uploadFileId || currentPdfId,
          });

          if (saveAnnotationFn && dbId) {
            try {
              const overlayText = overlay.querySelector('span')?.textContent || subjectText;

              let annotationData;
              try {
                if (overlayText.includes(' > ')) {
                  const parts = overlayText.split(' > ');
                  annotationData = {
                    area: parts[0],
                    category1: parts[1] || parts[0],
                    displayText: overlayText,
                  };
                } else {
                  annotationData = { displayText: overlayText };
                }
              } catch (parseError) {
                annotationData = { displayText: overlayText };
              }

              const viewer = document.getElementById('pdf-viewer') as any;
              const currentPage = viewer?.ej2_instances?.[0]?.currentPageNumber || 1;

              await saveAnnotationFn({
                variables: {
                  pdfDocumentAnnotationId: parseInt(dbId.toString()),
                  uploadFileId: uploadFileId || currentPdfId,
                  authorId: 1,
                  text: JSON.stringify(annotationData),
                  annotationPageNumber: currentPage,
                  xLocation: relativeX,
                  yLocation: relativeY,
                },
              });
              console.log('Position saved successfully for annotation', dbId);
            } catch (error) {
              console.error('Failed to save position for annotation', dbId, ':', error);
            }
          }
        }
      }
      isDragging = false;
    });

    return overlay;
  };

  const addCategoryAnnotation = async () => {
    if (!selectedArea || !selectedCategory1 || !currentQuestion) return;

    const viewer = document.getElementById('pdf-viewer') as any;
    if (!viewer?.ej2_instances?.[0]) return;

    const pdfViewer = viewer.ej2_instances[0];
    const categoryPath = [selectedArea, selectedCategory1, selectedCategory2, selectedCategory3]
      .filter(Boolean)
      .join(' > ');

    const lastCategory = selectedCategory3 || selectedCategory2 || selectedCategory1;
    const subjectText = `${selectedArea} > ${lastCategory}`;

    console.log('Creating annotation with text:', subjectText);

    const currentPage = pdfViewer.currentPageNumber;
    let pageWidth = 612;
    let pageHeight = 792;

    try {
      const pageElement = document.querySelector(`#pdf-viewer .e-pv-page-div[data-page-number="${currentPage}"]`);
      if (pageElement) {
        pageWidth = pageElement.clientWidth;
        pageHeight = pageElement.clientHeight;
      }
    } catch (error) {
      console.log('Could not get page dimensions, using defaults');
    }

    const centerX = pageWidth / 2;
    const centerY = pageHeight / 2;

    const container = document.querySelector('#pdf-viewer .e-pv-viewer-container');

    const annotationData = {
      area: selectedArea,
      category1: selectedCategory1,
      category2: selectedCategory2,
      category3: selectedCategory3,
      question: currentQuestion,
      displayText: subjectText,
      categoryPath: categoryPath,
    };

    try {
      await saveAnnotation({
        variables: {
          uploadFileId: currentPdfId,
          authorId: 1,
          text: JSON.stringify(annotationData),
          annotationPageNumber: currentPage,
          xLocation: centerX,
          yLocation: centerY,
        },
      });
      console.log('Annotation saved to backend:', subjectText);
    } catch (error) {
      console.error('Error saving annotation to backend:', error);
      showSnackbar('Error saving annotation', 'error', false);
      return; // Don't create DOM overlay if backend save failed
    }

    pdfViewer.annotationModule.setAnnotationMode('None');

    try {
      pdfViewer.annotationModule.addAnnotation('StickyNotes', {
        author: 'User',
        subject: subjectText,
        notes: JSON.stringify(annotationData),
        fillColor: '#FFFF00',
        strokeColor: '#FFFF00',
        opacity: 0.1,
        modifiedDate: new Date(),
        isLock: false,
        annotName: `category-annotation-${Date.now()}`,
        bounds: {
          X: centerX + 200,
          Y: centerY - 100,
          Width: 8,
          Height: 8,
          Angle: 0,
          PageNumber: currentPage,
        },
        pageNumber: currentPage,
      });

      console.log('PDF annotation created successfully');
    } catch (annotationError) {
      console.error('Error creating PDF annotation:', annotationError);
    }

    const overlay = createDOMOverlay(subjectText, `${centerX}px`, `${centerY}px`, null, saveAnnotation, currentPdfId);
    if (container) {
      container.appendChild(overlay);
    }

    setTimeout(() => {
      pdfViewer.annotationModule.setAnnotationMode('None');
    }, 100);

    setSelectedArea('');
    setSelectedCategory1('');
    setSelectedCategory2('');
    setSelectedCategory3('');
  };

  const loadAnnotations = async (pdfId: number) => {
    try {
      const { data } = await getAnnotations({
        variables: { uploadFileId: pdfId },
        fetchPolicy: 'network-only',
      });

      if (data?.getPdfDocumentAnnotations?.edges) {
        const waitForPdfViewer = () => {
          return new Promise(resolve => {
            let attempts = 0;
            const maxAttempts = 50;

            const checkViewer = () => {
              attempts++;
              const pdfViewer = document.getElementById('pdf-viewer') as any;
              const pdfContainer = document.querySelector('#pdf-viewer .e-pv-viewer-container');

              // Check if PDF viewer is properly initialized with all required properties
              if (pdfViewer?.ej2_instances?.[0] && pdfViewer.ej2_instances[0].pdfViewer && pdfContainer) {
                resolve({ pdfViewer, pdfContainer });
              } else if (attempts >= maxAttempts) {
                resolve({ pdfViewer: null, pdfContainer: null });
              } else {
                setTimeout(checkViewer, 100);
              }
            };
            checkViewer();
          });
        };

        const { pdfViewer, pdfContainer } = (await waitForPdfViewer()) as any;

        if (!pdfContainer || !pdfViewer) {
          console.error('❌ PDF viewer or container not available, cannot load annotations');
          return;
        }

        data.getPdfDocumentAnnotations.edges.forEach(edge => {
          const annotation = edge.node;

          if (annotation.text) {
            try {
              const annotationData = JSON.parse(annotation.text);
              if (annotationData.displayText) {
                const x = annotation.xLocation ? `${annotation.xLocation}px` : '50%';
                const y = annotation.yLocation ? `${annotation.yLocation}px` : '50%';

                const overlay = createDOMOverlay(
                  annotationData.displayText,
                  x,
                  y,
                  annotation.dbId,
                  saveAnnotation,
                  pdfId
                );

                pdfContainer.appendChild(overlay);
              }
            } catch (parseError) {
              const x = annotation.xLocation ? `${annotation.xLocation}px` : '50%';
              const y = annotation.yLocation ? `${annotation.yLocation}px` : '50%';

              const overlay = createDOMOverlay(annotation.text, x, y, annotation.dbId, saveAnnotation, pdfId);
              pdfContainer.appendChild(overlay);
            }
          }
        });

        const traditionalAnnotations = data.getPdfDocumentAnnotations.edges
          .filter(edge => edge.node.text && edge.node.text.includes('pdfAnnotation'))
          .map(edge => edge.node.text)[0];

        if (traditionalAnnotations && pdfViewer?.ej2_instances?.[0]?.importAnnotation) {
          try {
            pdfViewer.ej2_instances[0].importAnnotation(traditionalAnnotations);
          } catch (importError) {
            console.error('Error importing PDF annotations:', importError);
          }
        }
      } else {
      }
    } catch (error) {
      console.error('Error loading annotations:', error);
    }
  };

  const handleFileOpen = (args: any) => {
    console.log('File open event triggered:', args);

    if (args.fileDetails.isFile) {
      console.log('This is a file.');
    } else {
      console.log('This is not a file.');
    }

    // Check if `type` is `pdf` (backend returns extension without dot)
    if (args.fileDetails.type === 'pdf') {
      console.log('File is of type PDF.');
    } else if (!args.fileDetails.isFile) {
      console.log('File is not of type PDF; it is a folder  or other type.');
    } else {
      console.log(`File is not of type PDF; it is of type ${args}.`);
      // download the file
      // window.open(
      //   `${hostUrl}/${companyId}/download?path=${encodeURIComponent(args.fileDetails.path)}/${encodeURIComponent(args.fileDetails.name)}`,
      // );
      window.open(`${args.fileDetails.url}`);
    }
    if (args.fileDetails.isFile && args.fileDetails.type === 'pdf') {
      console.log('PDF file detected:', args.fileDetails.name);

      const fileId = parseInt(args.fileDetails.id, 10);

      // Show loading indicator
      setIsPdfLoading(true);

      // Fetch the PDF file through the file's URL (backend now returns bytes instead of redirecting)
      void fetch(args.fileDetails.url)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.blob();
        })
        .then(blob => {
          // Create a blob URL for the PDF viewer
          const blobUrl = URL.createObjectURL(blob);

          // Reset the annotations loaded flag for new PDF
          annotationsLoadedRef.current = false;

          // Set the new PDF data
          setCurrentPdfId(fileId);
          setPdfFileUrl(blobUrl);
        })
        .catch(error => {
          console.error('Error loading PDF:', error);
          showSnackbar('Failed to load PDF file', 'error', false);
        })
        .finally(() => {
          // Hide loading indicator
          setIsPdfLoading(false);
        });
    }
  };

  const clearExistingAnnotations = () => {
    const container = document.querySelector('#pdf-viewer .e-pv-viewer-container');
    if (container) {
      let clearedCount = 0;

      const dbOverlays = container.querySelectorAll('div[data-db-id]');
      dbOverlays.forEach(overlay => {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
          clearedCount++;
        }
      });

      const colorOverlays = container.querySelectorAll('div[style*="background-color: rgb(255, 255, 204)"]');
      colorOverlays.forEach(overlay => {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
          clearedCount++;
        }
      });

      const buttonOverlays = container.querySelectorAll('div button[style*="background: rgb(255, 68, 68)"]');
      buttonOverlays.forEach(button => {
        const overlay = button.closest('div[style*="position: absolute"]');
        if (overlay && overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
          clearedCount++;
        }
      });
    }
  };

  const closePdfViewer = () => {
    try {
      clearExistingAnnotations();

      const pdfViewer = document.getElementById('pdf-viewer') as any;
      if (pdfViewer && pdfViewer.ej2_instances && pdfViewer.ej2_instances[0]) {
        pdfViewer.ej2_instances[0].unload();
      } else {
        console.log('PDF viewer not found or not initialized.');
      }
    } catch (error) {
      console.error('Error closing PDF viewer:', error);
    }

    setPdfFileUrl(null);
    setCurrentPdfId(null);
    setIsFullscreen(false);
    annotationsLoadedRef.current = false; // Reset the flag when closing
  };

  const handleFileSelect = (args: FileSelectEventArgs) => {
    if (args && args.fileDetails) {
      setSelectedFileDetails(args.fileDetails as FileType);
    }
  };

  const handleContextMenuClick = async args => {
    if (!args.fileDetails?.length) return;

    const fileId = args.fileDetails[0].id;

    // Map menu item IDs to endpoint paths
    const endpointMap = {
      [ContextMenuItem.MARK_CUSTOMERLIST]: 'mark_as_benchmark_file',
      [ContextMenuItem.UNMARK_CUSTOMERLIST]: 'unmark_as_customer_file',
      [ContextMenuItem.MARK_AS_IM]: 'mark_as_im',
      [ContextMenuItem.MARK_AS_SHAREHOLDER]: 'mark_as_shareholder',
      [ContextMenuItem.UNMARK_AS_SHAREHOLDER]: 'unmark_as_shareholder',
      [ContextMenuItem.MARK_AS_XLS_CUSTOMER]: 'mark_as_xls_customer',
      [ContextMenuItem.UNMARK_AS_XLS_CUSTOMER]: 'unmark_as_xls_customer',
      [ContextMenuItem.MARK_AS_DA_UPLOAD]: 'mark_as_da_upload',
      [ContextMenuItem.UNMARK_AS_DA_UPLOAD]: 'unmark_as_da_upload',
    };

    const endpoint = endpointMap[args.item.id];
    if (!endpoint) {
      console.warn('Unknown context menu item:', args.item.id);
      return;
    }

    try {
      const response = await fetch(`${hostUrl}/${fileId}/${endpoint}`, {
        method: 'POST',
      });

      if (response.ok) {
        const isUnmark = endpoint.includes('unmark');
        showSnackbar(isUnmark ? 'File unmarked successfully' : 'File marked successfully', 'success', false);

        // Refresh file manager to show updated indicators
        if (fileManagerRef.current) {
          fileManagerRef.current.refreshFiles();
          // Trigger indicator refresh after a delay
          setTimeout(() => setFileIndicatorTrigger(prev => prev + 1), 600);
        }
      } else {
        throw new Error('Failed to update file');
      }
    } catch (error) {
      console.error('Error updating file:', error);
      showSnackbar(error.message || 'Error updating file', 'error', false);
    }
  };

  const handleContextMenuBeforeOpen = args => {
    // This event fires before the context menu opens
    // Use this to detect right-click on files and select them
    console.log('beforeMenuOpen args:', args);
  };

  const handleContextMenuOpen = args => {
    // Determine file type from the selected file or target element
    let customMenuItems: any[] = [];

    // Try to get file details from args or from the target element
    let fileDetails = args.fileDetails;

    console.log('menuOpen - fileDetails:', fileDetails);

    // If fileDetails is not available (direct right-click), try getting from the file manager
    if (!fileDetails || fileDetails.length === 0) {
      // Get the file manager instance
      const fileManager = fileManagerRef.current;
      if (fileManager) {
        // Try to access the file details from the file manager's internal state
        const selectedItems = fileManager.selectedItems;
        console.log('menuOpen - selectedItems from fileManager:', selectedItems);
        if (selectedItems && selectedItems.length > 0) {
          fileDetails = selectedItems;
        }
      }
    }

    // Only add custom menu items if we have file details and it's a file (not folder)
    if (fileDetails && fileDetails.length > 0 && fileDetails[0].isFile) {
      const fileType = fileDetails[0].type?.toLowerCase();

      if (fileType === 'pdf') {
        customMenuItems = pdfContextMenuItems;
      } else if (fileType === 'xlsx' || fileType === 'xls') {
        customMenuItems = excelContextMenuItems;
      }
    }

    args.items = [...args.items, ...customMenuItems];
  };

  const selectedFileModifiedDate =
    selectedFileDetails?.dateModified && new Date(selectedFileDetails.dateModified).toLocaleString();

  const metadataFileValue = (value: string | number) => value || '';

  // Modified export function
  const handleExportAnnotations = () => {
    // Simplified export - just show what's currently on screen
    const domOverlays = document.querySelectorAll('#pdf-viewer .e-pv-viewer-container > div');
    console.log('Current annotations on screen:', domOverlays.length);

    const annotationTexts = [];
    domOverlays.forEach(overlay => {
      const textSpan = overlay.querySelector('span');
      if (textSpan && textSpan.textContent) {
        annotationTexts.push(textSpan.textContent);
      }
    });

    if (annotationTexts.length > 0) {
      showSnackbar(`Found ${annotationTexts.length} annotations: ${annotationTexts.join(', ')}`, 'info', false);
    } else {
      showSnackbar('No annotations found on current page', 'warning', false);
    }

    console.log('Annotations already saved individually to backend');
  };

  // Add visual indicators after file list is loaded
  useEffect(() => {
    const addFileIndicators = () => {
      console.log('=== addFileIndicators called ===');
      const fileManager = fileManagerRef.current;

      if (!fileManager) {
        console.log('No fileManager instance found');
        return;
      }

      console.log('FileManager instance exists');

      // Find all file name cells in the grid
      const gridRows = document.querySelectorAll('#file-manager .e-grid .e-row');
      console.log(`Found ${gridRows.length} grid rows`);

      gridRows.forEach((row: any, index) => {
        console.log(`\n--- Processing row ${index} ---`);

        // Try to access file data from ej2 instance
        const fileData = row.ej2_instances?.[0]?.data;
        console.log('Row ej2_instances:', row.ej2_instances);
        console.log('File data from ej2:', fileData);

        if (!fileData) {
          console.log('No file data found in ej2 instance');
          return;
        }

        if (!fileData.isFile) {
          console.log('Item is not a file (likely a folder)');
          return;
        }

        console.log('File name:', fileData.name);
        console.log('Special attributes:', {
          is_pdf_dd_file: fileData.is_pdf_dd_file,
          is_pdf_shareholder_file: fileData.is_pdf_shareholder_file,
          is_xls_customer_file: fileData.is_xls_customer_file,
          is_xls_benchmarking_file: fileData.is_xls_benchmarking_file,
        });

        const hasSpecialAttribute =
          fileData.is_pdf_dd_file ||
          fileData.is_pdf_shareholder_file ||
          fileData.is_xls_customer_file ||
          fileData.is_xls_benchmarking_file;

        console.log('Has special attribute:', hasSpecialAttribute);

        if (hasSpecialAttribute) {
          // Find the name cell and add a star icon
          const nameCell = row.querySelector('.e-fe-text');
          console.log('Name cell found:', !!nameCell);

          if (nameCell && !nameCell.querySelector('.special-file-star')) {
            console.log('Adding star icon to:', fileData.name);
            const star = document.createElement('span');
            star.className = 'special-file-star';
            star.textContent = ' ★';
            star.style.color = '#FFD700';
            star.style.fontWeight = 'bold';
            star.style.marginLeft = '6px';
            star.title = 'Special file';
            nameCell.appendChild(star);
            console.log('Star icon added successfully');
          } else if (nameCell?.querySelector('.special-file-star')) {
            console.log('Star already exists for:', fileData.name);
          }
        }
      });

      console.log('=== addFileIndicators complete ===\n');
    };

    // Run after file list loads
    const timer = setTimeout(addFileIndicators, 500);
    return () => clearTimeout(timer);
  }, [companyId, fileIndicatorTrigger]); // Re-run when company changes or files are marked/unmarked

  return (
    <div className="company-files-page">
      <Typography variant="h4" gutterBottom>
        {t('companyFilesPage.title')}
      </Typography>
      <div className="control-section">
        {showFirstSyncOverlay ? (
          <Paper
            variant="outlined"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              minHeight: 320,
              p: 6,
              borderStyle: 'dashed',
            }}
          >
            <CircularProgress size={48} />
            <Box textAlign="center">
              <Typography variant="h6" gutterBottom>
                Synchronising files from Google Drive…
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This may take a few minutes depending on the number of files. The page will refresh automatically once
                synchronisation is complete.
              </Typography>
            </Box>
          </Paper>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {showNeverSyncedBanner && (
              <Alert severity="info" sx={{ mb: 1 }}>
                No files have been synchronised yet. Click the sync button below to load files from Google Drive.
              </Alert>
            )}
            {showResyncBanner && (
              <Box sx={{ mb: 1 }}>
                <LinearProgress />
                <Typography variant="caption" color="info.main" sx={{ mt: 0.5, display: 'block' }}>
                  Re-synchronising files from Google Drive — existing files are still accessible.
                </Typography>
              </Box>
            )}
            <StyledFileManager
              ref={fileManagerRef}
              id="file-manager"
              view="Details"
              // cssClass="e-custom-style" // use the style for cortex in general
              enableVirtualization={false}
              allowDragAndDrop={true}
              rootAliasName="Company Files"
              navigationPaneSettings={{
                maxWidth: '250px',
                minWidth: '150px',
                visible: true,
              }}
              uploadSettings={{
                autoClose: true,
                directoryUpload: false,
                minFileSize: 0,
                maxFileSize: 30000000,
                autoUpload: true,
              }}
              detailsViewSettings={{
                columns: [
                  {
                    field: 'name',
                    headerText: 'Name',
                    minWidth: 150,
                    width: 'auto',
                    customAttributes: { class: 'e-fe-grid-name' },
                  },
                  {
                    field: 'fileType',
                    headerText: '',
                    minWidth: 60,
                    width: '80',
                    textAlign: 'Center',
                    template:
                      '<div style="text-align: center;">${if(is_pdf_dd_file)}<strong>IM</strong>${else if(is_pdf_shareholder_file)}<strong>SL</strong>${else if(is_xls_customer_file)}<strong>CUST</strong>${else if(is_xls_benchmarking_file)}<strong>DA</strong>${else}${/if}</div>',
                  },
                  {
                    field: 'size',
                    headerText: 'Size',
                    minWidth: 50,
                    width: '100',
                    textAlign: 'Right',
                    template: '${if(isFile)}${sizeDisplay}${else}${/if}',
                  },
                  {
                    field: 'dateModified',
                    headerText: 'Date Modified',
                    minWidth: 130,
                    width: '150',
                    template: '${if(isFile)}${dateModifiedDisplay}${else}${/if}',
                  },
                ],
              }}
              menuClick={handleContextMenuClick}
              beforeMenuOpen={handleContextMenuBeforeOpen}
              menuOpen={handleContextMenuOpen}
              success={(args: any) => {
                // Format file sizes and dates after data is loaded
                if (args.result && args.result.files) {
                  args.result.files = args.result.files.map((file: any) => {
                    if (file.isFile) {
                      // Format size for display
                      if (file.size !== undefined) {
                        file.filterPath = file.filterPath || '';
                        file.sizeDisplay = formatFileSize(file.size);
                      }
                      // Format date for display
                      if (file.dateModified) {
                        file.dateModifiedDisplay = formatDate(file.dateModified);
                      }
                    }
                    return file;
                  });
                }
              }}
              ajaxSettings={{
                url: `${hostUrl}/${companyId}`, // Corrected interpolation
                downloadUrl: `${hostUrl}/${companyId}/download`, // Corrected interpolation
                uploadUrl: `${hostUrl}/${companyId}/upload`, // Corrected interpolation
                getImageUrl: `${hostUrl}/${companyId}/get-image`, // Corrected interpolation
              }}
              fileOpen={handleFileOpen}
              fileSelect={handleFileSelect}
            >
              <Inject services={[NavigationPane, Toolbar, DetailsView]} />
            </StyledFileManager>
          </Box>
        )}
      </div>

      {/* Google Drive Info Footer */}
      {companyNode && (
        <Box mt={2} mb={2}>
          {/* Drive selector */}
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              Drive:
            </Typography>
            {!companyNode.gdriveSharedDriveName ? (
              <Select
                size="small"
                displayEmpty
                value=""
                onChange={e => handleDriveSelect(e.target.value)}
                sx={{ fontSize: '0.75rem', minWidth: 120, height: 24 }}
              >
                <MenuItem value="" disabled>
                  <em>Select drive</em>
                </MenuItem>
                {availableDrives.map((drive: any) => (
                  <MenuItem key={drive.name} value={drive.name}>
                    {drive.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {companyNode.gdriveSharedDriveName}
              </Typography>
            )}
          </Box>

          {/* Directory input */}
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              Directory:
            </Typography>
            {!companyNode.gdriveDirectoryName && !isEditingDirectory ? (
              <Typography
                variant="caption"
                color="primary"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => setIsEditingDirectory(true)}
              >
                Click to configure
              </Typography>
            ) : isEditingDirectory ? (
              <Box display="flex" alignItems="center" gap={0.5}>
                <TextField
                  size="small"
                  value={directoryInput}
                  onChange={e => setDirectoryInput(e.target.value)}
                  placeholder="Enter directory name"
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', height: 24, padding: '0 8px' } }}
                />
                <Button size="small" onClick={handleDirectorySave}>
                  Save
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setIsEditingDirectory(false);
                    setDirectoryInput('');
                  }}
                >
                  Cancel
                </Button>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {companyNode.gdriveDirectoryName}
              </Typography>
            )}
          </Box>

          {/* Sync status */}
          {gdriveSyncStatus && (
            <Box display="flex" alignItems="center" gap={1}>
              {gdriveSyncStatus.lastSyncCompleted ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  Last synced: {format(new Date(gdriveSyncStatus.lastSyncCompleted), 'dd MMM yyyy HH:mm')}
                  {gdriveSyncStatus.totalFilesSynced > 0 && ` (${gdriveSyncStatus.totalFilesSynced} files)`}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary" display="block">
                  Last synced: Never
                </Typography>
              )}

              {/* Sync button */}
              <Tooltip title={isCooldownActive ? 'Sync is in cooldown period' : 'Trigger sync'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleRefreshSync}
                    disabled={isCooldownActive || gdriveSyncStatus.isSyncing}
                    sx={{ padding: 0.5 }}
                  >
                    <RefreshIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </span>
              </Tooltip>

              {gdriveSyncStatus.isSyncing && (
                <Typography variant="caption" color="info.main">
                  Syncing...
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Dialog to display PDF Viewer */}
      <Dialog
        open={!!pdfFileUrl}
        onClose={closePdfViewer}
        maxWidth={isFullscreen ? false : 'lg'}
        fullWidth
        fullScreen={isFullscreen}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>{t('companyFilesPage.area')}</InputLabel>
              <Select
                value={selectedArea}
                onChange={e => {
                  setSelectedArea(e.target.value);
                  setSelectedCategory1('');
                  setSelectedCategory2('');
                  setSelectedCategory3('');
                }}
                label="Area"
              >
                {areas.map(area => (
                  <MenuItem key={area} value={area}>
                    {area}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedArea && (
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>{t('companyFilesPage.category1')}</InputLabel>
                <Select
                  value={selectedCategory1}
                  onChange={e => {
                    setSelectedCategory1(e.target.value);
                    setSelectedCategory2('');
                    setSelectedCategory3('');
                  }}
                  label={t('companyFilesPage.category1')}
                >
                  {categories1.map(cat => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedCategory1 && (
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>{t('companyFilesPage.category2')}</InputLabel>
                <Select
                  value={selectedCategory2}
                  onChange={e => {
                    setSelectedCategory2(e.target.value);
                    setSelectedCategory3('');
                  }}
                  label={t('companyFilesPage.category2')}
                >
                  {categories2.map(cat => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedCategory2 && categories3.length > 0 && (
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>{t('companyFilesPage.category3')}</InputLabel>
                <Select
                  value={selectedCategory3}
                  onChange={e => setSelectedCategory3(e.target.value)}
                  label={t('companyFilesPage.category3')}
                >
                  {categories3.map(cat => (
                    <MenuItem key={cat} value={cat}>
                      <Tooltip title={currentQuestion}>
                        <span>{cat}</span>
                      </Tooltip>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Button
              variant="contained"
              onClick={addCategoryAnnotation}
              disabled={!currentQuestion}
              sx={{
                backgroundColor: currentQuestion ? '#4CAF50' : undefined,
                '&:hover': {
                  backgroundColor: currentQuestion ? '#45a049' : undefined,
                },
                minWidth: '160px',
                fontWeight: 'bold',
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: currentQuestion ? '0 2px 4px rgba(0,0,0,0.2)' : undefined,
              }}
            >
              {t('companyFilesPage.addAnnotation')}
            </Button>
            {currentQuestion && (
              <Tooltip title={currentQuestion} placement="top" arrow>
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Button variant="contained" color="primary" onClick={handleExportAnnotations} disabled={isSaving}>
            {isSaving ? t('companyFilesPage.savingAnnotations') : t('companyFilesPage.exportAnnotations')}
          </Button>

          {/* Fullscreen and Close buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              sx={{ color: 'primary.main' }}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
            <IconButton onClick={closePdfViewer} title="Close" sx={{ color: 'primary.main' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {isLoadingAnnotations ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          pdfFileUrl && (
            <PdfViewerComponent
              id="pdf-viewer"
              documentPath={pdfFileUrl}
              resourceUrl="https://cdn.syncfusion.com/ej2/24.1.41/dist/ej2-pdfviewer-lib"
              style={{ height: isFullscreen ? 'calc(100vh - 100px)' : '640px' }}
              enableAnnotation={true}
              enableAnnotationToolbar={false}
              enableTextSearch={false} // Disable text search to prevent the error
              enableTextSelection={true} // Keep text selection enabled
              documentLoad={(args: any) => {
                console.log('Document load event fired for PDF:', currentPdfId);
                // Ensure PDF is fully loaded before attempting any operations
                // Only load annotations once per PDF
                if (!annotationsLoadedRef.current && currentPdfId) {
                  const viewer = document.getElementById('pdf-viewer') as any;
                  if (viewer?.ej2_instances?.[0]) {
                    console.log('Setting up annotation loading for PDF:', currentPdfId);
                    annotationsLoadedRef.current = true; // Set flag immediately to prevent race condition

                    // Delay annotation loading to ensure document is ready
                    setTimeout(() => {
                      if (currentPdfId) {
                        console.log('Loading annotations for PDF:', currentPdfId);
                        loadAnnotations(currentPdfId);
                      }
                      // Re-enable text search after document is fully loaded
                      if (viewer?.ej2_instances?.[0]?.textSearchModule) {
                        viewer.ej2_instances[0].enableTextSearch = true;
                      }
                    }, 1000);
                  }
                }
              }}
              annotationSettings={
                {
                  author: 'User',
                  minimumZoom: 0.5,
                  maximumZoom: 3.0,
                  isLock: false,
                  isPrint: true,
                  annotationMode: 'None', // Set default mode to None to prevent double annotations
                } as AnnotationSettingsModel
              }
            >
              <Inject
                services={[
                  Toolbar,
                  Magnification,
                  Navigation,
                  Annotation,
                  LinkAnnotation,
                  BookmarkView,
                  ThumbnailView,
                  Print,
                  TextSelection,
                  // TextSearch,  // Remove TextSearch from services to prevent initialization errors
                  FormFields,
                  FormDesigner,
                ]}
              />
            </PdfViewerComponent>
          )
        )}
      </Dialog>

      {/* PDF Loading Indicator */}
      <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={isPdfLoading}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading PDF...
          </Typography>
        </Box>
      </Backdrop>

      {/* GDrive Configuration Dialog */}
      <GDriveConfigDialog
        open={showConfigDialog}
        onClose={handleConfigDialogClose}
        onConfirm={handleConfigConfirm}
        companyId={companyId ? parseInt(companyId) : 0}
        companyName={companyNode?.shortName || companyNode?.legalName}
        estimate={configEstimate}
      />
    </div>
  );
};

export default CompanyFilesPage;
